import React, { useState, useEffect } from 'react';
import TyreFilters from './components/TyreFilters';
import TyreList from './components/TyreList';
import TyreSegments from './components/TyreSegments';
import TyreService from './services/TyreService';
import './scss/App.scss';

const App = () => {
  const [tyres, setTyres] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [activeFilters, setActiveFilters] = useState({});
  const [sortType, setSortType] = useState('default'); // 'default', 'price_asc', 'price_desc'
  const [autoOpenModal, setAutoOpenModal] = useState(false);

  // Fonction pour lire les paramètres d'URL
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    
    // Liste des paramètres possibles
    const possibleParams = [
      'width', 'height', 'radius', 'brand', 'season',
      'category', 'runflat', 'reinforced', 'speedIndex', 'loadIndex', 'profile', 'profileSlug'
    ];
    
    possibleParams.forEach(param => {
      const value = params.get(param);
      if (value) {
        filters[param] = value;
      }
    });
    
    // Vérifier si on doit ouvrir automatiquement la modal du premier résultat
    const autoOpen = params.get('autoOpen');
    if (autoOpen === 'true') {
      setAutoOpenModal(true);
    }
    
    return filters;
  };

  // Effet pour charger les filtres initiaux depuis l'URL
  useEffect(() => {
    const urlFilters = getUrlParams();
    if (Object.keys(urlFilters).length > 0) {
      setActiveFilters(urlFilters);
      handleSearch(urlFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (filters) => {
    setActiveFilters(filters);
  };

  const handleSearch = async (filters) => {
    setLoading(true);
    setError(null);
    try {
      // Recherche des pneus
      const tyresResult = await TyreService.searchTyres({
        ...filters,
        page: currentPage
      });
      
      // Mise à jour avec la structure correcte de l'API
      setTyres(tyresResult.member || []);
      setTotalItems(tyresResult.totalItems || 0);

      // Recherche des segments (sans le filtre marque)
      const { brand, ...segmentFilters } = filters;
      const segmentsResult = await TyreService.getSegments(segmentFilters);
      setSegments(segmentsResult || []);
    } catch (err) {
      setError('Erreur lors de la recherche des pneus');
      console.error('Erreur recherche:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    handleSearch({ ...activeFilters, page: newPage });
  };

  // Fonction pour gérer le changement de tri
  const handleSortChange = (newSortType) => {
    setSortType(newSortType);
  };

  // Fonction pour appliquer le tri sur les données
  const getSortedTyres = (tyres, sortType) => {
    if (!tyres || tyres.length === 0) return tyres;

    const tyresToSort = [...tyres]; // Copie pour ne pas muter l'original

    switch (sortType) {
      case 'price_asc':
        return tyresToSort.sort((a, b) => {
          const priceA = a.price || 0;
          const priceB = b.price || 0;
          return priceA - priceB;
        });
      
      case 'price_desc':
        return tyresToSort.sort((a, b) => {
          const priceA = a.price || 0;
          const priceB = b.price || 0;
          return priceB - priceA;
        });
      
      case 'default':
      default:
        return tyres; // Retourne l'ordre original de l'API
    }
  };

  return (
    <div className="app">
      <TyreFilters 
        onFilterChange={handleFilterChange} 
        onSearch={handleSearch}
        initialFilters={activeFilters}
      />
	  {(!activeFilters.brand || activeFilters.brand === '') && (
        <TyreSegments 
          segments={segments} 
          loading={loading} 
          error={error}
        />
      )}
      <TyreList 
        tyres={getSortedTyres(tyres, sortType)}
        loading={loading}
        error={error}
        onPageChange={handlePageChange}
        currentPage={currentPage}
        totalItems={totalItems}
        sortType={sortType}
        onSortChange={handleSortChange}
        autoOpenModal={autoOpenModal}
        onModalOpened={() => setAutoOpenModal(false)}
      />
    </div>
  );
};

export default App;
